/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { IntakeFormDetailApiResponse } from '../models/IntakeFormDetailApiResponse';
import type { IntakeFormDocumentConfirmRequestDto } from '../models/IntakeFormDocumentConfirmRequestDto';
import type { IntakeFormDocumentPresignedRequestDto } from '../models/IntakeFormDocumentPresignedRequestDto';
import type { SubmitIntakeFormDto } from '../models/SubmitIntakeFormDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class ClientProfileService {
    /**
     * Calculate BMI
     * Calculates BMI from unit-based height/weight input, determines BMI category, computes healthy weight range, and stores latest BMI on the client profile.
     * @param requestBody Single endpoint with discriminated payload by unitSystem. Send metric object for metric or standard object for imperial inputs.
     * @returns any BMI calculated successfully
     * @throws ApiError
     */
    public static clientProfileControllerCalculateBmiV1(
        requestBody: ({
            unitSystem: 'metric';
            age: number;
            gender: 'male' | 'female';
            metric: {
                heightCm: number;
                weightKg: number;
            };
        } | {
            unitSystem: 'standard';
            age: number;
            gender: 'male' | 'female';
            standard: {
                heightFt: number;
                heightIn: number;
                weightLb: number;
            };
        }),
    ): CancelablePromise<{
        data?: {
            bmi?: number;
            bmiCategory?: string;
            unitSystem?: 'metric' | 'standard';
            weightKg?: number;
            heightCm?: number;
            age?: number;
            gender?: string;
            healthyWeightRange?: {
                minKg?: number;
                maxKg?: number;
            };
            bmiLastCalculated?: string;
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/client-profile/bmi',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden - Only clients can calculate BMI`,
            },
        });
    }
    /**
     * Get BMI
     * Returns bmi and bmi_last_calculated from clients table.
     * @returns any BMI fetched successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetBmiV1(): CancelablePromise<{
        data?: {
            bmi?: number | null;
            bmiLastCalculated?: string | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/bmi',
            errors: {
                403: `Forbidden - Only clients can access BMI`,
            },
        });
    }
    /**
     * Get intake forms for a client
     * Returns list of all intake forms for the specified client. Intake forms are auto-created based on: (1) General Information form for all clients, (2) Service-specific forms matching service_type_ids stored in client preferences during onboarding, (3) Forms for service types the client has bookings with.
     * @returns any Intake forms fetched successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetIntakeFormsV1(): CancelablePromise<{
        success?: boolean;
        message?: string;
        data?: Array<{
            id?: string;
            formName?: string;
            serviceTypeName?: string;
            expertName?: string | null;
            isCompleted?: boolean;
            completedAt?: string | null;
            createdAt?: string;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/intake-forms',
            errors: {
                404: `Client not found`,
            },
        });
    }
    /**
     * Get my diet plans
     * Returns list of diet plans assigned to the authenticated client. Figma: Diet Plans screen.
     * @returns any Diet plans fetched successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetDietPlansV1(): CancelablePromise<{
        data?: {
            items?: Array<{
                id?: string;
                planName?: string;
                status?: 'active' | 'completed';
                createdAt?: string;
                creatorName?: string;
                durationDays?: number;
                targetCalories?: number | null;
                startDate?: string;
                updatedAt?: string;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/diet-plans',
            errors: {
                403: `Forbidden - Only clients can access diet plans`,
            },
        });
    }
    /**
     * Get diet plan detail
     * Returns full diet plan detail including description, nutritional goals, weekly meal plan, and notes. Figma: View Plan screen.
     * @param id Diet plan ID (UUID)
     * @returns any Diet plan detail fetched successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetDietPlanDetailV1(
        id: string,
    ): CancelablePromise<{
        data?: {
            id?: string;
            planName?: string;
            status?: string;
            description?: string | null;
            creatorName?: string;
            createdAt?: string;
            durationDays?: number;
            targetCalories?: number | null;
            nutritionalGoals?: Record<string, any> | null;
            weeklyMealPlan?: Record<string, any> | null;
            allergies?: string | null;
            preferences?: string | null;
            specialInstructions?: Array<string> | null;
            startDate?: string;
            endDate?: string;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/diet-plans/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `Forbidden - Only clients can access diet plan details`,
                404: `Diet plan not found`,
            },
        });
    }
    /**
     * Delete account permanently
     * Permanently deletes the user account and all associated data. This action cannot be undone.
     * @returns any Account deleted successfully
     * @throws ApiError
     */
    public static clientProfileControllerDeleteAccountV1(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/v1/client-profile/delete-account',
            errors: {
                403: `Forbidden - Only clients can delete their account`,
            },
        });
    }
    /**
     * Get presigned URL for intake form document
     * Returns a presigned URL for uploading a document (e.g. X-ray, MRI, reports) for the Physiotherapist form. Client uploads to S3, then calls the confirm endpoint. Store fileKey/url in responses when submitting the form.
     * @param id Intake form response ID (UUID)
     * @param requestBody
     * @returns any Presigned URL generated successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetDocumentPresignedUrlV1(
        id: string,
        requestBody: IntakeFormDocumentPresignedRequestDto,
    ): CancelablePromise<{
        statusCode?: number;
        status?: string;
        message?: string;
        data?: {
            provider?: 'AWS' | 'AZURE';
            uploadUrl?: string;
            fileKey?: string;
            expiresIn?: number;
            fields?: Record<string, any>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/client-profile/{id}/documents/presigned-url',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden - Not the client for this form`,
                404: `Not found`,
            },
        });
    }
    /**
     * Confirm intake form document upload
     * Confirms the file was uploaded to S3. Returns fileKey, url, fileName to store in form responses.reports when submitting.
     * @param id Intake form response ID (UUID)
     * @param requestBody
     * @returns any Document upload confirmed successfully
     * @throws ApiError
     */
    public static clientProfileControllerConfirmDocumentUploadV1(
        id: string,
        requestBody: IntakeFormDocumentConfirmRequestDto,
    ): CancelablePromise<{
        statusCode?: number;
        status?: string;
        message?: string;
        data?: {
            fileKey?: string;
            url?: string;
            fileName?: string;
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/client-profile/{id}/documents/confirm',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request - File not found or invalid file key`,
                403: `Forbidden - Not the client for this form`,
                404: `Not found`,
            },
        });
    }
    /**
     * Get signed download URL for intake form document
     * Returns a fresh signed URL when the previous one has expired.
     * @param id Intake form response ID (UUID)
     * @param fileKey S3 file key (from presigned-url/confirm response)
     * @returns any Signed download URL generated successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetDocumentDownloadUrlV1(
        id: string,
        fileKey: string,
    ): CancelablePromise<{
        statusCode?: number;
        status?: string;
        message?: string;
        data?: {
            fileKey?: string;
            url?: string;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/{id}/documents/url',
            path: {
                'id': id,
            },
            query: {
                'fileKey': fileKey,
            },
            errors: {
                400: `Bad Request - Invalid file key`,
                403: `Forbidden - Not the client for this form`,
                404: `Not found`,
            },
        });
    }
    /**
     * Get intake form detail
     * Returns full form detail including formSchema (JSON-driven form definition) and responses (saved answers). Frontend renders the form dynamically from formSchema.
     * @param id Intake form response ID (UUID)
     * @returns IntakeFormDetailApiResponse Intake form detail fetched successfully
     * @throws ApiError
     */
    public static clientProfileControllerGetIntakeFormDetailV1(
        id: string,
    ): CancelablePromise<IntakeFormDetailApiResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/client-profile/intake-forms/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `Forbidden - Not the client for this form`,
                404: `Not found`,
            },
        });
    }
    /**
     * Submit intake form
     * Save draft (isCompleted=false) or submit/lock (isCompleted=true) the form. If isCompleted=true: sets completed_at and notifies the expert. For file upload fields (e.g. Reports in Physiotherapist form), use POST /v1/client-profile/:id/documents/presigned-url and /documents/confirm, then include in responses.
     * @param id Intake form response ID (UUID)
     * @param requestBody
     * @returns IntakeFormDetailApiResponse Intake form saved successfully
     * @throws ApiError
     */
    public static clientProfileControllerSubmitIntakeFormV1(
        id: string,
        requestBody: SubmitIntakeFormDto,
    ): CancelablePromise<IntakeFormDetailApiResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/v1/client-profile/intake-forms/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation failed (required fields missing when submitting) or form already completed`,
                403: `Forbidden - Not the client for this form`,
                404: `Not found`,
            },
        });
    }
}
